// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Table2, Image, Sparkles, Archive, Gift, Lock } from 'lucide-react';
import { api } from '../utils/api.js';
import Logo from '../components/shared/Logo.jsx';
import PaywallModal from '../components/shared/PaywallModal.jsx';
import UploadZone from '../components/upload/UploadZone.js';

export default function Upload() {
  const navigate  = useNavigate();
  const [uploading,  setUploading]  = useState(false);
  const [error,      setError]      = useState('');

  const [userPlan,       setUserPlan]       = useState(null);
  const [previewUsed,    setPreviewUsed]    = useState(false);
  const [userLoading,    setUserLoading]    = useState(true);
  const [showPaywall,    setShowPaywall]    = useState(false);

  useEffect(() => {
    api.getMe()
      .then(d => {
        setUserPlan(d.plan || 'free');
        setPreviewUsed(d.preview_used ?? false);
      })
      .catch(() => {
        setUserPlan('free');
        setPreviewUsed(false);
      })
      .finally(() => setUserLoading(false));
  }, []);

  const isFree         = userPlan === 'free';
  const showPreviewBanner = isFree && !previewUsed && !userLoading;
  const showLockedState   = isFree && previewUsed && !userLoading;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-bg)' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      {/* Header */}
      <header style={{ background: 'var(--color-surface)', borderBottom: '1px solid var(--color-border)' }}>
        <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 24px', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Logo iconSize={24} onClick={() => navigate('/dashboard')} />
          <button onClick={() => navigate('/dashboard')} style={ghostBtn}>← Dashboard</button>
        </div>
      </header>

      <main style={{ maxWidth: 560, margin: '0 auto', padding: '48px 24px' }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 6 }}>Upload Catalog</h1>
        <p style={{ color: 'var(--color-text-2)', marginBottom: 24, lineHeight: 1.6 }}>
          Upload your supplier files (PDF, Excel, CSV, images, or ZIP) and we'll extract the product data for your Shopify store.
        </p>

        {/* Free preview notice */}
        {showPreviewBanner && (
          <div style={{
            background: '#EFF6FF', border: '1px solid #BFDBFE',
            borderRadius: 10, padding: '12px 16px', marginBottom: 20,
            display: 'flex', alignItems: 'flex-start', gap: 10,
          }}>
            <Gift size={18} style={{ color: '#1D4ED8', flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#1D4ED8', marginBottom: 2 }}>
                First catalog free — no credit card required
              </div>
              <div style={{ fontSize: 13, color: '#1E40AF', lineHeight: 1.5 }}>
                Upload any catalog and see your products extracted instantly.
                Full review table and first export included free — no credit card required.
              </div>
            </div>
          </div>
        )}

        {error && (
          <div style={{ background: 'var(--color-red-bg)', border: '1px solid var(--color-red-border)', color: 'var(--color-red-text)', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 14 }}>
            {error}
          </div>
        )}

        {/* Locked state for exhausted preview */}
        {showLockedState ? (
          <>
          {showPaywall && (
            <PaywallModal
              onClose={() => setShowPaywall(false)}
              title="Upgrade to keep processing catalogs"
              message="Your first catalog is free. Upgrade to a paid plan to process more catalogs."
              trigger="upload"
            />
          )}
          <div style={{
            border: '2px dashed var(--color-border)',
            borderRadius: 14, padding: '56px 32px', textAlign: 'center',
            background: 'var(--color-surface)', marginBottom: 24,
          }}>
            <div style={{ marginBottom: 12 }}><Lock size={44} style={{ color: 'var(--color-text-2)' }} /></div>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Ready to process more catalogs?</div>
            <p style={{ fontSize: 14, color: 'var(--color-text-2)', maxWidth: 340, margin: '0 auto 24px', lineHeight: 1.6 }}>
              Your first catalog is free. Upgrade to a paid plan to process more catalogs.
            </p>
            <button
              onClick={() => setShowPaywall(true)}
              style={primaryBtn}
            >
              See plans &amp; pricing
            </button>
          </div>
          </>
        ) : (
          <UploadZone
            uploadFn={api.uploadMulti}
            onUploadComplete={(result) => {
              if (result.catalog_id && result.job_id) {
                // Auto-bridged: go straight to review with extraction banner
                navigate(`/review/${result.catalog_id}?job_id=${result.job_id}`);
              } else if (result.catalog_drop_id) {
                // Needs decision (2+ catalog PDFs) or no auto-bridge: drops page
                navigate(`/drops/${result.catalog_drop_id}`);
              }
            }}
            disabled={uploading}
          />
        )}

        {/* Format info */}
        <div style={{ marginTop: 32, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {[
            { Icon: FileText, label: 'PDF', desc: 'Scanned or text-based catalogs' },
            { Icon: Table2, label: 'Excel / CSV', desc: 'Structured product spreadsheets' },
            { Icon: Image, label: 'Images', desc: 'JPG, PNG, WebP catalog pages' },
            { Icon: Sparkles, label: 'AI extraction', desc: 'Powered by Claude & Mistral' },
          ].map(item => (
            <div key={item.label} style={{
              background: 'var(--color-surface)', border: '1px solid var(--color-border)',
              borderRadius: 10, padding: '14px 16px',
              display: 'flex', alignItems: 'flex-start', gap: 10,
            }}>
              <item.Icon size={20} strokeWidth={1.5} style={{ color: 'var(--color-primary)', flexShrink: 0, marginTop: 1 }} />
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>{item.label}</div>
                <div style={{ fontSize: 12, color: 'var(--color-text-2)', lineHeight: 1.4 }}>{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

const primaryBtn = {
  width: '100%', background: 'var(--color-primary)', color: '#fff',
  border: 'none', borderRadius: 10, padding: '14px',
  fontSize: 16, fontWeight: 700, cursor: 'pointer',
};
const ghostBtn = {
  background: 'none', border: '1px solid var(--color-border)',
  borderRadius: 8, padding: '6px 14px', fontSize: 13,
  color: 'var(--color-text-2)', cursor: 'pointer',
};
